import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft, ArrowRight } from "lucide-react";
import { InterestForm } from "../components/InterestForm";

// About — a public, read-only page describing what Commons is, why it
// exists, how it works, and who is behind it. Intentionally kept as a
// natural extension of the app shell: shares the phone-shell frame,
// the Commons eyebrow/serif typography, and the sticky back row used
// elsewhere. No external links, no mailing lists, no imagined team.
//
// Accessible without a session; renders outside <Gate/> so a paused
// or still-provisioning visitor can always reach it.
const scrollToInvolved = (behavior) =>
  document.getElementById("get-involved")?.scrollIntoView({ behavior, block: "start" });

export default function About() {
  const nav = useNavigate();
  const { hash } = useLocation();
  useEffect(() => {
    if (hash === "#get-involved") scrollToInvolved("auto");
  }, [hash]);
  const jumpToInvolved = (e) => {
    e.preventDefault();
    window.history.replaceState(null, "", "#get-involved");
    scrollToInvolved("smooth");
  };
  return (
    <div className="c-page c-about-page" data-testid="about-page">
      <div className="c-page-topbar">
        <button
          type="button"
          className="c-back-row"
          onClick={() => nav("/")}
          data-testid="about-back-btn"
          aria-label="Back to the demo"
        >
          <ChevronLeft size={18} /> Back to the demo
        </button>
      </div>
      <a
        href="#get-involved"
        className="c-about-jump"
        onClick={jumpToInvolved}
        data-testid="about-get-involved-link"
      >
        Get involved ↗
      </a>

      <header className="c-about-hero">
        <div className="c-eyebrow">About Commons</div>
        <h1 className="c-h1 c-about-h1">
          What would you do if enough people would help?
        </h1>
        <p className="c-about-lead">
          Commons is an experiment in making collective action easier.
        </p>
      </header>

      <section className="c-about-section" data-testid="about-what">
        <h2 className="c-h2 c-about-h2">What is Commons?</h2>
        <p>
          Many things we'd like to do become possible if enough people
          participate. Organizing a neighborhood event, starting a
          cooperative, improving a park, or changing a law might require
          dozens, hundreds, or thousands of people.
        </p>
        <p>
          The challenge isn't always convincing people that something is
          worth doing. Sometimes plenty of people are already willing, but
          nobody knows who else is.
        </p>
        <p>
          Commons is an attempt to make that willingness visible and help
          people act on it.
        </p>
      </section>

      <section className="c-about-section" data-testid="about-how">
        <h2 className="c-h2 c-about-h2">How it works</h2>
        <p>Commons is built around a few simple ideas:</p>

        <div className="c-about-idea">
          <div className="c-about-idea-title">Conditional commitments</div>
          <p>
            Instead of asking people to commit to something that may never
            happen, Commons lets them say, “I'm willing to do this if enough
            other people are too.”
          </p>
        </div>

        <div className="c-about-idea">
          <div className="c-about-idea-title">Thresholds</div>
          <p>
            Projects move forward when enough people have committed.
            Thresholds don't create willingness. They reveal it.
          </p>
        </div>

        <div className="c-about-idea">
          <div className="c-about-idea-title">Distributed recruiting</div>
          <p>
            Participants can invite people they know, who can invite others
            in turn. Commons includes an experimental visualization called{" "}
            <em>Roots</em> — developed as part of Commons — that makes these
            recruiting relationships visible.
          </p>
        </div>

        <div className="c-about-idea">
          <div className="c-about-idea-title">Temporary coordination</div>
          <p>
            People can take on responsibilities when they're needed, without
            requiring every project to become a permanent organization.
          </p>
        </div>
      </section>

      <section className="c-about-section" data-testid="about-experiment">
        <h2 className="c-h2 c-about-h2">An ongoing experiment</h2>
        <p>
          Commons is still in development. The projects and participants in
          the current demo are illustrative.
        </p>
        <p>
          The goal isn't simply to build another organizing platform. It's
          to explore whether better tools and simpler social rules can make
          doing ambitious things together a more ordinary part of everyday
          life.
        </p>
        <p>
          The next step is to experiment with real people and real projects,
          learn what works, and improve the tools accordingly.
        </p>
      </section>

      <section className="c-about-section" data-testid="about-who">
        <h2 className="c-h2 c-about-h2">Who's behind Commons?</h2>
        <p>
          Commons was initiated by Bartosz Kumor, drawing on nearly twenty
          years of experience in community and political organizing.
        </p>
        <p>
          The project has also benefited from people who participated in
          earlier experiments with conditional commitments and distributed
          organizing, tested prototypes, offered feedback, and expressed
          interest in future experiments.
        </p>
        <p>
          Commons is not currently a formal organization or an established
          team. It's a growing collection of experiments and collaborators.
        </p>
      </section>

      <section id="get-involved" className="c-about-section c-about-involved" data-testid="about-involved">
        <h2 className="c-h2 c-about-h2">Get involved</h2>
        <p>
          Interested in experimenting with Commons, trying it with a real
          project, contributing ideas, or comparing notes?
        </p>
        <p>Leave a note and we'll be in touch.</p>
        <InterestForm />
      </section>

      <div className="c-about-return">
        <button
          type="button"
          className="c-btn c-btn-primary"
          onClick={() => nav("/")}
          data-testid="about-return-btn"
        >
          Return to the demo <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}
